<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  ArrowLeft,
  Calendar,
  Briefcase,
  User,
  Phone,
  Mail,
  ChevronRight,
  CalendarClock,
  Ban,
} from 'lucide-vue-next';
import { visitsAPI, type Visit } from '../services/api';
import { serviceNames } from '../utils/visit';
import { serverMessage } from '../composables/useVisits';
import { formatVisitAtLong } from '../utils/date';
import VisitRescheduleModal from '../components/VisitRescheduleModal.vue';
import VisitCancelDialog from '../components/VisitCancelDialog.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const visit = ref<Visit | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

const visitAt = computed(() => (visit.value ? formatVisitAtLong(visit.value.visit_at) : ''));

const isArchived = computed(() =>
  visit.value ? new Date(visit.value.visit_at).getTime() < Date.now() : false,
);

const userName = computed(() => {
  const u = visit.value?.user;
  return u ? `${u.first_name} ${u.last_name}`.trim() : '—';
});

const isRescheduleOpen = ref(false);
const isCancelOpen = ref(false);

// The moved visit comes back from the API, so the card refreshes without a refetch.
const handleRescheduled = (updated: Visit) => {
  visit.value = { ...visit.value, ...updated };
};

onMounted(async () => {
  const id = route.params.id as string;
  isLoading.value = true;
  error.value = null;

  try {
    visit.value = (await visitsAPI.getById(id)).data;
  } catch (err: any) {
    error.value = serverMessage(err) || t('composables.visits.error_fetch_one');
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col min-h-full bg-[#0A0A0B]">
    <div class="flex-1 p-4 md:p-6 lg:p-8">
      <div class="max-w-3xl mx-auto">
        <!-- Back link -->
        <button
          @click="router.push('/visits')"
          class="flex items-center gap-2 text-sm text-[#524840] hover:text-[#F5F0EA] mb-6 transition"
        >
          <ArrowLeft class="w-4 h-4" />
          {{ t('visits.back_to_list') }}
        </button>

        <!-- Loading -->
        <div
          v-if="isLoading"
          class="bg-[#111113] rounded-xl border border-[#2A2018] p-8 text-center text-[#524840]"
        >
          {{ t('visits.loading_one') }}
        </div>

        <!-- Error -->
        <div
          v-else-if="error"
          class="p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400"
        >
          {{ error }}
        </div>

        <template v-else-if="visit">
          <!-- Visit card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6 mb-4">
            <div class="flex items-start justify-between gap-3 mb-6">
              <div>
                <h2 class="text-2xl font-bold text-[#F5F0EA]">{{ t('visits.detail_title') }}</h2>
                <p class="text-[#524840] mt-1">{{ visitAt }}</p>
              </div>
              <span
                :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
                  isArchived ? 'bg-[#1E1E23] text-[#9E9080]' : 'bg-green-900/30 text-green-400',
                ]"
              >
                {{ isArchived ? t('visits.status_past') : t('visits.status_upcoming') }}
              </span>
            </div>

            <!-- Only a visit still ahead of us can be moved or called off. -->
            <div v-if="!isArchived" class="flex flex-wrap items-center gap-2 mb-6">
              <button
                type="button"
                class="flex items-center gap-2 py-2 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-[#F5F0EA] rounded-xl text-sm font-medium transition"
                @click="isRescheduleOpen = true"
              >
                <CalendarClock class="w-4 h-4 text-[#D4956A]" />
                {{ t('visits.reschedule') }}
              </button>
              <button
                type="button"
                class="flex items-center gap-2 py-2 px-4 bg-[#1E1E23] hover:bg-[#2A2018] text-red-400 rounded-xl text-sm font-medium transition"
                @click="isCancelOpen = true"
              >
                <Ban class="w-4 h-4" />
                {{ t('visits.cancel_visit') }}
              </button>
            </div>

            <dl class="space-y-4">
              <div class="flex items-center gap-3">
                <Calendar class="w-4 h-4 text-[#D4956A] flex-shrink-0" />
                <dt class="text-sm text-[#524840] w-28 flex-shrink-0">
                  {{ t('visits.visit_at') }}
                </dt>
                <dd class="text-sm text-[#F5F0EA]">{{ visitAt }}</dd>
              </div>
              <div class="flex items-center gap-3">
                <Briefcase class="w-4 h-4 text-[#D4956A] flex-shrink-0" />
                <dt class="text-sm text-[#524840] w-28 flex-shrink-0">
                  {{ t('visits.services') }}
                </dt>
                <dd class="text-sm text-[#F5F0EA]">
                  {{ serviceNames(visit) }}
                  <span v-if="visit.duration_minutes" class="text-[#9E9080]">
                    ({{ visit.duration_minutes }} {{ t('visits.minutes') }})
                  </span>
                </dd>
              </div>
              <div class="flex items-center gap-3">
                <User class="w-4 h-4 text-[#D4956A] flex-shrink-0" />
                <dt class="text-sm text-[#524840] w-28 flex-shrink-0">{{ t('visits.user') }}</dt>
                <dd class="text-sm text-[#F5F0EA]">{{ userName }}</dd>
              </div>
            </dl>
          </div>

          <!-- Client card -->
          <div class="bg-[#111113] rounded-xl border border-[#2A2018] p-6">
            <h3 class="text-lg font-semibold text-[#F5F0EA] mb-4">
              {{ t('visits.client_details') }}
            </h3>

            <div v-if="visit.client" class="space-y-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-10 h-10 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0"
                >
                  <span class="text-teal-300 text-sm font-medium">
                    {{ visit.client.name[0]?.toUpperCase() }}
                  </span>
                </div>
                <span class="text-sm font-medium text-[#F5F0EA]">{{ visit.client.name }}</span>
              </div>

              <div class="flex items-center gap-3">
                <Phone class="w-4 h-4 text-[#D4956A] flex-shrink-0" />
                <span class="text-sm text-[#F5F0EA]">
                  {{ visit.client.data?.phone_number || t('visits.no_phone') }}
                </span>
              </div>

              <div class="flex items-center gap-3">
                <Mail class="w-4 h-4 text-[#D4956A] flex-shrink-0" />
                <span class="text-sm text-[#F5F0EA]">
                  {{ visit.client.data?.email || t('visits.no_email') }}
                </span>
              </div>

              <button
                @click="router.push(`/clients/${visit.client.id}`)"
                class="flex items-center gap-1 text-sm text-[#D4956A] hover:text-[#E8B48A] transition pt-2"
              >
                {{ t('visits.open_client') }}
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>

            <p v-else class="text-sm text-[#524840]">—</p>
          </div>
        </template>
      </div>
    </div>

    <VisitRescheduleModal
      :open="isRescheduleOpen"
      :visit="visit"
      @close="isRescheduleOpen = false"
      @rescheduled="handleRescheduled"
    />

    <VisitCancelDialog
      :visit="isCancelOpen ? visit : null"
      @close="isCancelOpen = false"
      @cancelled="router.push('/visits')"
    />
  </div>
</template>
